import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import { useNavigationBar } from "../../../context/NavigationContext";
import { SKPollsArtifact, ContentState, PollsAnswerType } from "../../../models/SKArtifact";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import { AddDropdownField } from "./container/components/AddDropdownField";
import { AddCheckBoxSet, CheckBoxData } from "./container/components/AddCheckBoxSet";
import { SKSelectOption } from "../../../components/SKSelectOption";
import { AddTextField } from "../../../components/AddTextField";
import { generateUUID } from "../../../utils/stringFunctions";

interface Options {
  title: string;
  value: string | number | readonly string[] | undefined;
}

export default function AddPolls() {
  const { contentData, artifactId, updateContentData, loading, saveData, isEditable } = useContext(CreateArtifactContext);
  const { setNavProps } = useNavigationBar();
  const navigate = useNavigate();
  // const [isEdit, setIsEdit] = useState(false);
  // const [editable, setEditable] = useState<boolean>(true);
  // const [dropDownData, setDropDownData] = useState<string[]>([
  //   "24 Hrs",
  //   "3 Hrs",
  //   "7 Days",
  // ]);
  const [dropDownData, setDropDownData] = useState<Options[]>([
    { title: "24 Hrs", value: 24 },
    { title: "3 Hrs", value: 3 },
    { title: "7 Days", value: 168 },
    { title: "No Expiration", value: undefined },
  ]);
  const [pollesDuration, setPollesDuration] = useState<string>("24 Hrs");
  const [pollesQuestion, setPollesQuestion] = useState<string>("");
  // const [pollOptions, setPollOptions] = useState<string[]>(["", ""]);
  const [checkBoxData, setCheckBoxData] = useState<CheckBoxData[]>([
    {
      mode: PollsAnswerType.single,
      name: "Allow only one answer",
      notes: "People would be able to choose only one option.",
    },
    {
      mode: PollsAnswerType.multi,
      name: "Allow multiple answers",
      notes: "People would be able to choose multiple options.",
    },
  ]);

  const MIN_OPTIONS = 2;
  const MAX_OPTIONS = 7;

  const [isValidForm, setIsValidForm] = useState(false);
  const [pollsData, setPollsData] = useState<SKPollsArtifact | undefined>(undefined);

  const pollOptionSchema = Yup.object({
    text: Yup.string().required("Text is required"),
    count: Yup.number().required("Count is required").min(0, "Count cannot be negative"),
  });

  const pollsOptionsItemsSchema = Yup.object()
    .shape({
      // Use `Yup.object()` with `.shape()` to validate dynamic keys
    })
    .test("is-valid-pollsOptionsItems", "Invalid pollsOptionsItems", value => {
      if (!value || typeof value !== "object") return false;
      return Object.values(value).every(item => pollOptionSchema.isValidSync(item));
    });

  const schema = Yup.object().shape({
    pollDuration: Yup.number().notRequired(),
    pollQuestion: Yup.string().required("Poll question is required."),
    pollAnswerType: Yup.string().required("Poll answer type is required."),
    // pollsOptionsItems: Yup.object()
    //   .shape({
    //     key: Yup.number().required("Index is required."),
    //     name: Yup.string().required("Name is required."),
    //   })
    //   .required("Poll answer type is required."),
    pollsOptionsItems: pollsOptionsItemsSchema.required("pollsOptionsItems is required"),
  });

  useEffect(() => {
    // {
    //   contentData.status !== ContentState.IN_REVIEW ? setIsEdit(false) : setIsEdit(true);
    // }
    const validate = async () => {
      try {
        await schema.validate(contentData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err) {
        let error = err as Yup.ValidationError;
        // setErrors(prev => ({ ...prev, [name]: error.message }));
        setIsValidForm(false);
        if (error.inner) {
          const errors: { path: string; message: string }[] = error.inner.map(error1 => ({
            path: error1.path || "unknown", // Field with the error
            message: error1.message, // Error message
          }));

          console.log("Validation errors:", errors);
        }
      }
    };
    validate();
  }, [contentData, schema]);

  // useEffect(() => {
  //   if (contentData.status === ContentState.DRAFT) {
  //     setEditable(true);
  //   } else {
  //     setEditable(false);
  //   }
  // }, [contentData]);

  const submit = useCallback(async () => {
    await saveData();
    navigate(-1);
  }, []);

  useEffect(() => {
    setNavProps(prev => ({
      ...prev,
      backArrow: false,
      buttonTitle: "Done",
      title: "Add Poll",
      getActionItems: undefined,
      enableLink: isEditable ? isValidForm : false,
      onNext: isEditable ? submit : undefined,
      onClose: () => navigate(-1),
      onEdit: undefined,
    }));
  }, [setNavProps, pollsData, contentData.status, isValidForm, isEditable]);

  useEffect(() => {
    const pollArtifact = contentData as SKPollsArtifact;
    if (!pollArtifact.pollAnswerType) {
      updateContentData({ pollAnswerType: PollsAnswerType.single });
    }
    if (!pollArtifact.pollsOptionsItems) {
      updateContentData({
        ...pollArtifact,
        pollsOptionsItems: {
          [generateUUID()]: { text: "", count: 0 },
          [generateUUID()]: { text: "", count: 0 },
        },
      });
    } else {
      setPollsData(pollArtifact);
    }
  }, [contentData]);

  const handleChange = (key: string, value: string) => {
    if (pollsData?.pollsOptionsItems) {
      let obj = pollsData?.pollsOptionsItems;

      if (obj && obj[key]) {
        obj[key].text = value;
        isSecureContext && updateContentData({ pollsOptionsItems: obj });
      }
    }
  };
  // const handleChange = (key: string, value: string) => {
  //   if (pollsData?.pollsOptionsItems) {
  //     let obj = pollsData.pollsOptionsItems;

  //     // Optional chaining to safely update `text`
  //     obj[key]?.text && (obj[key].text = value);

  //     isSecureContext && updateContentData({ pollsOptionsItems: obj });
  //   }
  // };

  function convertNumberToWord(num: number): string {
    const numberWords: { [key: number]: string } = {
      1: "First",
      2: "Second",
      3: "Thired",
      4: "Fourth",
      5: "Fifth",
      6: "Sixth",
      7: "Seventh",
    };

    return numberWords[num] || "Invalid number";
  }

  const addOptionField = () => {
    const obj = { ...pollsData?.pollsOptionsItems, [generateUUID()]: { text: "", count: 0 } };
    isEditable && updateContentData({ pollsOptionsItems: obj });
  };

  const removeOption = (key: string) => {
    const obj = { ...pollsData?.pollsOptionsItems, [key]: undefined };

    isEditable && updateContentData({ pollsOptionsItems: obj });
  };

  return (
    <>
      <div className="flex flex-col mt-[12px] gap-[30px]">
        <div className="flex items-center">
          <SKSelectOption
            title={"Add Poll Duration"}
            heading="Add Poll Duration"
            index={1}
            value={(contentData as SKPollsArtifact).pollDuration}
            subHeading={"Poll will automatically end after the selected period of time, and then results will be displayed."}
            width={"w-[290px]"}
            onUpdate={async e => {
              setPollesDuration(e.target.value);
              isEditable && updateContentData({ pollDuration: e.target.value });
            }}
            marginBottom="mb-[0px]"
            options={dropDownData}
            textFont="notes-class-content"
            editable={isEditable}
          />
        </div>
        <div className="flex flex-col">
          <AddTextField
            name={"pollquestion"}
            title={"Add Poll Question"}
            note={"Maximum text length of 125 characters."}
            placeholder={"Poll Question"}
            index={2}
            charAllowed={125}
            value={(contentData as SKPollsArtifact).pollQuestion}
            onChange={async e => {
              setPollesQuestion(e.target.value);
              isEditable && updateContentData({ pollQuestion: e.target.value });
            }}
            disabled={!isEditable}
          />
        </div>
        <div className="flex flex-col">
          <AddCheckBoxSet
            index={3}
            title={"Add Poll Answer Type"}
            note={"Allow people to either choose only one option or more than one options."}
            answerType={checkBoxData}
            onSelect={function (selected: CheckBoxData | null): void {
              isEditable && updateContentData({ pollAnswerType: selected?.mode });
            }}
            setDefaultValue={
              (contentData as SKPollsArtifact).pollAnswerType
                ? checkBoxData.findIndex(item => item.mode === (contentData as SKPollsArtifact).pollAnswerType)
                : 0
            }
          />
        </div>
        <div className="flex flex-col">
          <div className="relative w-full min-w-[200px]">
            <div className="flex items-center">
              <span>
                <span className="content_count">4</span>
              </span>
              <span className="content_title">Add Poll Options</span>
            </div>
            <div className="notes-class-content">Minimum 2 options and maximum 7 options.</div>
            <div className="flex flex-col gap-[30px]">
              {pollsData?.pollsOptionsItems &&
                Object.keys(pollsData?.pollsOptionsItems).map((key, index) => (
                  <AddTextField
                    key={index}
                    placeholder={convertNumberToWord(index + 1) + " Option"}
                    charAllowed={50}
                    isCloseBtn={index >= 2 ? true : false}
                    index={index + 1}
                    value={pollsData.pollsOptionsItems[key].text}
                    onChange={e => isEditable && handleChange(key, e.target.value)}
                    onClose={() => removeOption(key)}
                    disabled={!isEditable}
                  />
                ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <div
            className={`relative w-full min-w-[200px] mb-2 ${
              isEditable && pollsData && pollsData.pollsOptionsItems && Object.keys(pollsData.pollsOptionsItems).length < MAX_OPTIONS
                ? "text-black cursor-pointer"
                : "text-gray-400"
            }`}
            onClick={isEditable ? addOptionField : undefined}
          >
            <div className="flex items-center space-x-2">
              <span className="title_inner_page-vom">+ Add Options</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
